import { ArrowLeft, ArrowRight, Building2, Home, MapPin } from "lucide-react";
import useSWR from "swr";

import api from "../../../../lib/api";
import { formatDisplayDate } from "../../../../utils/Date";
import type { BenchRow, HighCourtRow } from "./types";
import {
  CourtHeader,
  EmptyRow,
  IconBox,
  LoadingRow,
  MobileCardList,
  MobileInfoRow,
  ResponsiveTableShell,
  RowActions,
  StatusPill,
} from "./SharedUi";

interface BenchesProps {
  highCourtId: string;
  onBack: () => void;
  onAdd: (highCourt: HighCourtRow) => void;
  onOpen: (bench: BenchRow) => void;
  onEdit: (bench: BenchRow, highCourt: HighCourtRow) => void;
  onDelete: (bench: BenchRow) => void;
}

export const Benches = ({
  highCourtId,
  onBack,
  onAdd,
  onOpen,
  onEdit,
  onDelete,
}: BenchesProps) => {
  const { data: highCourtResponse, isLoading: highCourtsLoading } = useSWR(
    "/api/court/get-highcourt",
  );
  const highCourts = (highCourtResponse?.data || []) as HighCourtRow[];
  const highCourt = highCourts.find((court) => court._id === highCourtId);
  const { data: benchResponse, isLoading: benchesLoading } = useSWR(
    highCourtId
      ? `/api/court/get-benches/${highCourtId}?page=1&limit=1000`
      : null,
  );
  const benches = ((benchResponse?.status === false
    ? []
    : benchResponse?.data) || []) as BenchRow[];
  const { data: benchRoomCounts = {} } = useSWR(
    benches.length
      ? `new-courts-bench-room-counts:${benches
          .map((bench) => bench._id)
          .join("|")}`
      : null,
    async () => {
      const entries = await Promise.all(
        benches.map(async (bench) => {
          const response = await api.get(
            `/api/court/get-bench-rooms/${bench._id}?page=1&limit=1`,
          );
          const payload = response?.data;
          const count =
            payload?.pagination?.total ??
            payload?.pagination?.count ??
            (payload?.status === false ? 0 : payload?.data?.length || 0);

          return [bench._id, count] as const;
        }),
      );

      return Object.fromEntries(entries) as Record<string, number>;
    },
  );
  const totalCourtHalls = Object.values(benchRoomCounts).reduce(
    (total, count) => total + count,
    0,
  );
  const isLoading = highCourtsLoading || benchesLoading;

  if (!highCourt) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-10 text-center text-slate-500">
        {isLoading ? "Loading high court..." : "High court not found"}
      </div>
    );
  }

  return (
    <div>
      <button
        type="button"
        onClick={onBack}
        className="mb-5 inline-flex items-center gap-2 text-blue-700"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to High Courts
      </button>

      <CourtHeader
        icon={<Home className="h-6 w-6" />}
        title={highCourt.highCourtName}
        badge="Active"
        meta={[
          `${highCourt.capitalCity}, ${highCourt.stateName}`,
          `Est. ${formatDisplayDate(highCourt.established)}`,
          highCourt.stateCode,
        ]}
        stats={[
          { label: "Benches", value: benches.length },
          { label: "Court Halls", value: totalCourtHalls },
        ]}
      />

      <div className="mb-3 mt-8 inline-flex items-center gap-3 rounded-lg bg-blue-600 px-4 py-2 text-white">
        <Building2 className="h-4 w-4" />
        Benches
        <span className="rounded-full bg-white/20 px-2 text-sm">
          {benches.length}
        </span>
      </div>

      <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-slate-600">
          Benches under {highCourt.highCourtName}. Click a bench to manage its
          court halls.
        </p>
        {/* <PrimaryButton label="Add Bench" onClick={() => onAdd(highCourt)} /> */}
      </div>

      <ResponsiveTableShell>
        <table className="w-full min-w-[780px]">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-widest text-slate-500">
            <tr>
              <th className="px-6 py-4">Bench Name</th>
              <th className="px-6 py-4">Location</th>
              <th className="px-6 py-4">Status</th>
              <th className="px-6 py-4">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {isLoading && <LoadingRow colSpan={4} />}
            {benches.length ? (
              benches.map((bench) => (
                <tr key={bench._id} className="hover:bg-slate-50">
                  <td className="px-6 py-5">
                    <button
                      type="button"
                      onClick={() => onOpen(bench)}
                      className="flex items-center gap-4 text-left"
                    >
                      <IconBox icon={<Building2 className="h-5 w-5" />} />
                      <span>
                        <span className="block font-semibold text-blue-700">
                          {bench.benchName}
                          <ArrowRight className="ml-2 inline h-4 w-4" />
                        </span>
                        <span className="text-sm text-slate-400">
                          Click to manage court halls
                        </span>
                      </span>
                    </button>
                  </td>
                  <td className="px-6 py-5">
                    <span className="inline-flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-slate-400" />
                      {bench.location || "N/A"}
                    </span>
                  </td>
                  <td className="px-6 py-5">
                    <StatusPill status={bench.status} />
                  </td>
                  <td className="px-6 py-5">
                    <RowActions
                      onEdit={() => onEdit(bench, highCourt)}
                      onDelete={() => onDelete(bench)}
                      deleteDisabled
                    />
                  </td>
                </tr>
              ))
            ) : (
              <EmptyRow
                colSpan={4}
                label="Add benches to this high court"
                actionLabel="Add Bench"
                onAction={() => onAdd(highCourt)}
                hide
                hideLabel="No benches found"
              />
            )}
          </tbody>
        </table>
      </ResponsiveTableShell>

      <MobileCardList
        isLoading={isLoading}
        isEmpty={benches.length === 0}
        emptyLabel="No benches found"
        actionLabel="Add Bench"
        onAction={() => onAdd(highCourt)}
      >
        {benches.map((bench) => (
          <article
            key={bench._id}
            className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
          >
            <div className="flex items-start justify-between gap-3">
              <button
                type="button"
                onClick={() => onOpen(bench)}
                className="flex min-w-0 items-start gap-3 text-left"
              >
                <IconBox icon={<Building2 className="h-5 w-5" />} />
                <span className="min-w-0">
                  <span className="block font-semibold text-blue-700">
                    {bench.benchName}
                    <ArrowRight className="ml-1 inline h-4 w-4" />
                  </span>
                  <span className="text-sm text-slate-400">
                    Click to manage court halls
                  </span>
                </span>
              </button>
              <RowActions
                onEdit={() => onEdit(bench, highCourt)}
                onDelete={() => onDelete(bench)}
                deleteDisabled
              />
            </div>
            <div className="mt-4 space-y-3">
              <MobileInfoRow
                label="Location"
                value={
                  <span className="inline-flex items-center justify-end gap-2">
                    <MapPin className="h-4 w-4 text-slate-400" />
                    {bench.location || "N/A"}
                  </span>
                }
              />
              <MobileInfoRow
                label="Status"
                value={<StatusPill status={bench.status} />}
              />
            </div>
          </article>
        ))}
      </MobileCardList>
    </div>
  );
};
